/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation } from "react-router";
import queryString from "query-string";
import { Box, Button, Divider, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { push } from "connected-react-router";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { connect, useDispatch } from "react-redux";
import produce from "immer";
import * as billActions from "../../../actions/bill";
import * as bookingActions from "../../../actions/booking";
import * as clientActions from "../../../actions/client";
import {
  getBillNumberType,
  getTemplateBillItem,
  hasFiscalYear2079Started,
  mergeServicesProducts
} from "../../../actions/helpers/billHelper";
import * as serviceActions from "../../../actions/services";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import { tl } from "../../../components/translate";
import { BillItemType, DocumentTypes, VisitType } from "../../../interfaces/BillInterfaces";
import { IThunkDispatch, RootState, store } from "../../../store";
import BillHeader from "./BillHeader";
import BillItems from "./BillItems";
import { generateSubItems } from "./BillProductItem";
import BillSummary from "./BillSummary";
import BillEditorActions from "./BilllEditorActions";
import { RequestForm, getIpdRequestForm } from "../../../api/requestForms";
import { notificationAdd } from "../../../actions/notification";
import { productsGetByIds } from "../../../api/stockProducts";
import { getCreditableQuantities } from "../../../api/bill";
import useIsSsfBilling from "../../../hooks/useIsSsfBilling";
import { ProductType } from "../../Stock/StockCreateEdit/CreateStockProduct";
import { useAppSelector } from "../../../store/hooks";
import { setWarning } from "../../../actions/bill";
import { useSelectedServiceSubType } from "../../../hooks/useSelectedServceSubTypes";
import { ProductInterface } from "../../../interfaces/ProductInterface";
import { getIpdDetailsByClientId } from "../../../api/ipd";

import { commonErrorMessage } from "../../../helpers/messages";
import { ServiceInterface } from "../../../interfaces/ServiceInterface";
import { useBeds } from "../../../hooks/query/useBeds";

// @ts-expect-error - css
import "./BillEditor.scss";

const SSF_SERVICE_ORIGIN = "ssf";

export const LOCALSTORAGE_BILL = "billDraft";

export enum BILL_EDITOR_WARNING_TYPE {
  COUNTER = "COUNTER"
}

export interface BillEditorWarning {
  message: string;
  type: BILL_EDITOR_WARNING_TYPE | null;
}

interface BillEditorProps {
  actions: Record<string, (value?: unknown) => void>;
  mode: string;
  billId: number;
  clientId: number;
  bookingId: number;
  visitType: string;
  bedId: number;
  ipdId: number;
  labRequestId: number;
  productMedicationRequestId: number;
  purchaseRequisitionFormId: number;
  isProformaBill: boolean;
}

const BillEditor = ({
  actions,
  mode,
  billId,
  clientId,
  bookingId,
  visitType,
  bedId,
  ipdId,
  labRequestId,
  productMedicationRequestId,
  purchaseRequisitionFormId,
  isProformaBill = false
}: BillEditorProps) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // custom hooks
  const { data: beds = [] } = useBeds();
  const { isSsfBilling } = useIsSsfBilling();
  const { selectedSubTypes } = useSelectedServiceSubType();

  // selectors
  const bookings = useAppSelector((state) => state.bookings);
  const draft = useAppSelector((state) => state.bills.draft);
  const userContext = useAppSelector((state) => state.userContext);
  const services = useAppSelector((state) => state.services);
  const stockProducts = useAppSelector((state) => state.stockProducts);
  const warning = useAppSelector((state) => state.bills.warning);

  // variables
  const canAccessStockList = userContext.policies["stock:listStock"];
  const canAccessServiceList = userContext.policies["services:listService"];

  // refs
  const unsavedBill = React.useRef(null);
  const lastItemRef = React.useRef<HTMLInputElement | null>(null);
  const addBillItemButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // memo
  const products = React.useMemo(
    () =>
      mergeServicesProducts(
        canAccessServiceList ? services.collection : [],
        canAccessStockList ? stockProducts.collection : [],
        draft?.isSsfBilling
      ),
    [
      canAccessServiceList,
      canAccessStockList,
      services.collection,
      stockProducts.collection,
      draft?.isSsfBilling
    ]
  );

  // local states
  const [showDialog, setShowDialog] = React.useState(false);
  const [labRequest, setLabRequest] = React.useState<RequestForm | null>(null);
  const [filteredProducts, setFilteredProducts] = React.useState(products || []);
  const [productMedicationRequest, setProductMedicationRequest] =
    React.useState<RequestForm | null>(null);
  const [creditableQuantities, setCreditableQuantities] = React.useState<Record<string, number>>(
    {}
  );

  // helpers
  const focusAddItemButton = () => addBillItemButtonRef.current?.focus();
  const hasBillItems = (billItems) => billItems?.some((billItem) => Boolean(billItem.description));
  const currentBooking = bookingId ? bookings.collection.find(({ id }) => id === bookingId) : {};

  const params = queryString.parse(location.search);

  // effects
  React.useEffect(() => {
    let filtered = products.filter(({ origin, productType }) =>
      isSsfBilling
        ? origin === SSF_SERVICE_ORIGIN || productType === ProductType.Sellable
        : origin !== SSF_SERVICE_ORIGIN
    );

    if (selectedSubTypes?.length) {
      filtered = filtered.filter((item: ServiceInterface | ProductInterface) =>
        item.subType ? selectedSubTypes.includes(item.subType) : false
      );
    }

    setFilteredProducts(filtered);
  }, [products, isSsfBilling, selectedSubTypes]);

  React.useEffect(() => {
    (async () => {
      if (purchaseRequisitionFormId) {
        dispatch(billActions.createBillFromPurchaseRequisitionForm(purchaseRequisitionFormId));
      }
    })();
  }, [purchaseRequisitionFormId]);

  React.useEffect(() => {
    if (draft && draft.type === DocumentTypes.CREDITNOTE && clientId) {
      actions.clearDraft().then(() => actions.initDraft(clientId));
    }

    if (!billId && !draft && !purchaseRequisitionFormId) {
      actions.initDraft(clientId, visitType, bedId, beds, ipdId, isSsfBilling, isProformaBill);
    }

    if (billId && mode === "edit") {
      actions.loadBillForEdit(billId);
    }

    if (draft && clientId && Number(draft.client.id) !== Number(clientId)) {
      actions.initDraft(clientId, visitType, bedId, beds, ipdId, isSsfBilling, isProformaBill);
    }

    // init proforma bill if isProformaBill is true
    // cientId, bedId, ipdId are must for proforma bill if we are being redirected from ipd/beds
    if (isProformaBill && clientId && bedId && ipdId && visitType === VisitType.IPD) {
      actions.initDraft(clientId, visitType, bedId, beds, ipdId, isSsfBilling, isProformaBill);
    }

    actions.loadServices().then(async () => {
      if (bookingId) {
        actions.fetchBooking(bookingId);
      }
      if (labRequestId) {
        try {
          const reqDetails = await getIpdRequestForm(labRequestId);
          setLabRequest(reqDetails);
        } catch (error) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: "Failed to get selected lab request for bill.",
              autoTimeout: false
            })
          );
        }
      }

      if (productMedicationRequestId) {
        try {
          const reqDetails = await getIpdRequestForm(productMedicationRequestId);
          setProductMedicationRequest(reqDetails);
        } catch (error: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: error?.message || "Failed to get selected medication request for bill.",
              autoTimeout: false
            })
          );
        }
      }
    });

    const incompleteBill = JSON.parse(localStorage.getItem(LOCALSTORAGE_BILL));

    if (
      incompleteBill &&
      mode === "create" &&
      incompleteBill.draft.type !== DocumentTypes.CREDITNOTE &&
      !hasBillItems(draft?.billItems) &&
      (!clientId || clientId === incompleteBill.draft.client.id)
    ) {
      unsavedBill.current = incompleteBill;
      setShowDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (draft && draft.visitType !== visitType) {
      actions.updateDraft({ ...draft, visitType });
    }

    if (draft && bookingId && currentBooking) {
      actions.applyProductsFromBooking(draft, currentBooking, products);
    }

    if (draft && labRequest && labRequestId) {
      actions.applyServicesFromLabRequest(draft, labRequest, products);
    }

    if (draft && productMedicationRequest) {
      actions.applyProductsFromLabRequest(draft, productMedicationRequest);
    }
  }, [currentBooking, bookingId, labRequest, labRequestId, productMedicationRequest, visitType]);

  React.useEffect(() => {
    if (mode === "create" && hasBillItems(draft?.billItems) && !params.isFromBillingPackage) {
      localStorage.setItem(
        LOCALSTORAGE_BILL,

        JSON.stringify({
          draft
        })
      );
    }
  }, [draft, mode]);

  React.useEffect(() => {
    let timerRef: ReturnType<typeof setTimeout> | null = null;
    if (warning.message) {
      timerRef = setTimeout(() => {
        dispatch(setWarning({ message: "", type: null }));
      }, 10000);
    }
    return () => {
      if (timerRef !== null) {
        clearTimeout(timerRef);
      }
    };
  }, [warning]);

  React.useEffect(() => {
    if (draft?.issueDate && hasFiscalYear2079Started() && !draft?.isProformaBill) {
      const billNumberType = getBillNumberType(draft);
      actions.getNextBillNumber(billNumberType, draft.issueDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.issueDate, draft?.type, draft?.isProformaBill, actions]);

  React.useEffect(() => {
    (async () => {
      if (
        draft &&
        bedId &&
        draft.bedId &&
        draft.visitType === VisitType.IPD &&
        draft.type !== DocumentTypes.CREDITNOTE
      ) {
        try {
          const ipdDetails = await getIpdDetailsByClientId(draft.client?.id);

          // IMPORTANT:
          // Don't use the `draft` captured by this effect because it may
          // have become stale while awaiting the API call.
          //
          // Read the latest draft from the Redux store immediately before
          // dispatching the update to avoid stale-closure bugs.
          dispatch((_: unknown, getState: () => RootState) => {
            const latestDraft = getState().bills.draft;

            if (!latestDraft) return;

            dispatch(
              billActions.updateDraftAction({
                ...latestDraft,
                bedId: +bedId,
                bed: beds.find((bed) => bed.id === +bedId),
                ipdId: ipdDetails.id,
                admissionDate: ipdDetails.dateOfAdmission || "",
                dischargeDate: ipdDetails.dateOfDischarge || ""
              })
            );
          });
        } catch (error) {
          dispatch(
            notificationAdd({
              id: Date.now(),
              message: commonErrorMessage,
              variant: "error",
              autoTimeout: true
            })
          );
        }
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bedId, draft?.bedId, beds.length, draft?.client?.id]);

  React.useEffect(() => {
    if (draft && draft?.referenceTo && draft?.type === DocumentTypes.CREDITNOTE) {
      (async () => {
        try {
          // Fetch creditable quantities data
          const data = await getCreditableQuantities(draft.referenceTo);
          setCreditableQuantities(data);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: err?.message || "Failed to get creditable quantity for bill items.",
              autoTimeout: true
            })
          );
        }
      })();
    }
  }, []);

  return (
    <Box>
      {draft && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* main content: header and summary */}
          <Box
            sx={{
              height: "calc(100vh - 150px)",
              overflowY: "auto"
            }}
          >
            <Box sx={{ position: "sticky", top: 0, background: "white", zIndex: 2 }}>
              <BillHeader
                isCreditNote={draft.type === DocumentTypes.CREDITNOTE}
                draft={draft}
                updateDraft={actions.updateDraft}
                visitType={visitType as VisitType}
              />
              <Divider />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <BillItems
                lastItemRef={lastItemRef}
                focusAddItemButton={focusAddItemButton}
                isCreditNote={draft.type === DocumentTypes.CREDITNOTE}
                draft={draft}
                updateDraft={actions.updateDraft}
                onRowRemove={actions.onRowRemove}
                products={filteredProducts}
                creditableQuantities={creditableQuantities}
                addBillItemButtonRef={addBillItemButtonRef}
                onItemAdd={actions.onItemAdd}
              />

              <Collapse in={Boolean(warning.message)}>
                <Box sx={{ px: 2, pt: 2 }}>
                  <Alert
                    severity="error"
                    variant="outlined"
                    action={
                      <Button
                        onClick={() => {
                          dispatch(setWarning({ message: "", type: null }));
                        }}
                        size="medium"
                      >
                        OK
                      </Button>
                    }
                  >
                    <Box>
                      <Typography component="span" mr={1}>
                        {warning.message}
                      </Typography>
                      {warning.type === BILL_EDITOR_WARNING_TYPE.COUNTER && (
                        <Typography
                          component="span"
                          style={{ textDecoration: "underline", cursor: "pointer" }}
                        >
                          Open Settings
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                </Box>
              </Collapse>
            </Box>

            <Divider />

            <Box>
              <BillSummary
                isCreditNote={draft.type === DocumentTypes.CREDITNOTE}
                draft={draft}
                updateDraft={actions.updateDraft}
              />
            </Box>
          </Box>

          {/* bill editor actions */}

          <BillEditorActions
            draft={draft}
            creditableQuantities={creditableQuantities}
            isCreditNote={draft.type === DocumentTypes.CREDITNOTE}
          />
        </Box>
      )}

      {/* panels and dialogs */}
      <>
        <OkhatiDialog
          open={showDialog}
          title={tl("billing.recoverUnsavedBill")}
          description={`Unsaved bill found for ${unsavedBill?.current?.draft?.client?.firstName} ${unsavedBill?.current?.draft?.client?.lastName}. Do you want to recover it?`}
          next={() => {
            if (unsavedBill.current?.draft?.visitType === VisitType.IPD) {
              dispatch(push(`/billing/ipd`));
            } else {
              dispatch(push(`/billing/new`));
            }
            actions.updateDraft({ ...unsavedBill.current?.draft, isLocalStorageData: true });
            setShowDialog(false);
          }}
          cancel={() => {
            localStorage.removeItem(LOCALSTORAGE_BILL);

            setShowDialog(false);
          }}
          readMode={false}
        />
      </>
    </Box>
  );
};

export default connect(null, (dispatch: IThunkDispatch) => ({
  actions: {
    loadBillForEdit: (billId) => dispatch(billActions.loadBillForEdit(billId)),
    initDraft: async (clientId, visitType, bedId, beds, ipdId, isSsfBilling, isProformaBill) => {
      const { userContext } = store.getState();
      if (!isSsfBilling) {
        dispatch(
          clientActions.getWalkInClient(userContext.resourceCentreId, (draft, client) => {
            if (draft && !clientId) {
              // TODO: this code runs even if client is selected and i refresh and restore the bill
              dispatch(billActions.updateDraftAction({ ...draft, client, visitType }));
              dispatch(billActions.getClientBalance(client.id));
            }
          })
        );
      }
      const { clients } = store.getState();
      const hasSelectedClient = clients.collection?.some((client) => client.id === clientId);
      if (!hasSelectedClient && clientId) {
        await dispatch(clientActions.getClientById(clientId));
        await dispatch(billActions.getClientBalance(clientId));
      }

      await dispatch(
        billActions.initDraft({
          clientId,
          visitType,
          bedId,
          bed: beds.find((bed) => bed.id === +bedId),
          ipdId,
          isSsfBilling,
          isProformaBill
        })
      );

      dispatch((dispatchInner, getState) => {
        const { draft } = getState().bills;
        if (draft?.isDraft) {
          const billNumberType = getBillNumberType(draft);
          dispatchInner(
            billActions.getNextBillNumber(billNumberType, draft.issueDate, "initDraft")
          );
        }
      });
    },
    getNextBillNumber: async (type?: string, date?: string) => {
      dispatch((dispatchInner, getState) => {
        const { draft } = getState().bills;
        if (draft?.isDraft) {
          const billNumberType = type || getBillNumberType(draft);
          dispatchInner(
            billActions.getNextBillNumber(
              billNumberType,
              date || draft.issueDate,
              "getNextBillNumber"
            )
          );
        }
      });
    },
    clearDraft: async () => dispatch(billActions.clearDraft()),
    onItemAdd: () => dispatch(billActions.addNewRow()),
    updateDraft: (draft) => {
      dispatch(billActions.updateDraftAction(draft));
    },
    updateSettings: (settings) => {
      dispatch(billActions.updateDraftSettings(settings));
    },
    onRowRemove: (idx: number) => dispatch(billActions.removeRow(idx)),
    loadServices: async () => {
      await Promise.all([dispatch(serviceActions.getServices())]);
    },
    loadCustomers: () => dispatch(clientActions.getClients()),
    navigateTo: (url) => dispatch(push(url)),
    fetchBooking: (bookingId) => dispatch(bookingActions.getBooking(bookingId)),
    applyProductsFromBooking: (draft, booking, products) => {
      if (!booking || !booking.services) {
        return;
      }
      const newDraft = cloneDeep(draft);
      newDraft.referredBy = booking.referredBy;
      newDraft.referrerId = booking.referrerId;
      newDraft.billItems = [];
      booking.services.forEach((serviceId, idx) => {
        const product = products.find((p) => Number(p.id) === Number(serviceId));
        let serviceProviderId = null;
        if (product?.productType === "single") {
          serviceProviderId = product?.document?.rates?.find(
            (item) => item.serviceProviderId === booking.serviceProviderId
          )?.serviceProviderId;
        }

        if (product) {
          const billItem = {
            ...getTemplateBillItem(),
            sNo: 1 + idx,
            description: product.name,
            calculationBasis: "perUnit",
            perUnit: product.grossTotalPrice,
            productType: product?.productType,
            productId: product.id,
            ...(serviceProviderId ? { serviceProviderIds: [serviceProviderId] } : {}),
            serviceProviderRateUnit: product.serviceProviderRateUnit,
            ...(product?.document?.products && product.productType === "package"
              ? generateSubItems(product?.document?.products, products, booking.serviceProviderId)
              : {}),
            isMultiSession: product?.isMultiSession || false
          };
          newDraft.billItems.push(billItem);
        }
      });
      if (!newDraft.billItems.length) newDraft.billItems = cloneDeep(draft.billItems);
      dispatch(billActions.updateDraftAction(newDraft));
    },
    applyServicesFromLabRequest: (draft, labRequest, services) => {
      if (!labRequest || !labRequest.requestedItems?.length) {
        return;
      }
      const newDraft = cloneDeep(draft);
      newDraft.billItems = [];
      labRequest.requestedItems.forEach((service, idx) => {
        const product = services?.find((p) => Number(p.id) === Number(service.id)) || service;
        const productRates = product.document?.rates || product.rates || [];
        const packageProducts = product.document?.products || product.products || [];
        if (product) {
          const billItem = {
            ...getTemplateBillItem(),
            sNo: 1 + idx,
            description: product.name,
            calculationBasis: "perUnit",
            perUnit: product.grossTotalPrice,
            productId: product.id,
            salesLedgerId: product.salesLedgerId,
            departmentId: product.departmentId,
            salesTaxationId: product.salesTaxationId,
            productData: {
              rates: productRates
            },
            serviceProviderRateUnit: product.serviceProviderRateUnit,
            ...(packageProducts.length && product.productType === "package"
              ? generateSubItems(packageProducts, services, null)
              : {}),
            isMultiSession: product?.isMultiSession || false
          };
          newDraft.billItems.push(billItem);
        }
      });
      if (!newDraft.billItems.length) newDraft.billItems = cloneDeep(draft.billItems);
      dispatch(billActions.updateDraftAction(newDraft));
    },
    applyProductsFromLabRequest: async (draft, labRequest) => {
      const productIds = labRequest.requestedItems.map((item) => item.id);
      const products = await productsGetByIds(productIds);
      const billItems = [] as BillItemType[];
      products.forEach((product, idx) => {
        const billItem = {
          ...getTemplateBillItem(),
          sNo: 1 + idx,
          description: product.name,
          calculationBasis: "perUnit",
          perUnit: product.grossTotalPrice,
          productId: product.id,
          salesLedgerId: product.salesLedgerId,
          departmentId: product.departmentId,
          salesTaxationId: product.salesTaxationId,
          productData: {
            rates: product.rates
          },
          serviceProviderRateUnit: product.serviceProviderRateUnit,
          isMultiSession: product?.isMultiSession || false
        };
        billItems.push(billItem);
      });

      const newDraft = produce(draft, (draftItem) => {
        draftItem.billItems = billItems;
      });
      dispatch(billActions.updateDraftAction(newDraft));
    }
  }
}))(BillEditor);
